"use client";

import _slicedToArray from "/home/circleci/project/web-front/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { ComboboxListRoleContext, ComboboxScopedContextProvider, useComboboxContext, useComboboxScopedContext } from "./3LH6HMTR.js";
import { isHidden } from "./UYRBEP7M.js";
import { createElement, createHook, forwardRef } from "./7FZLUSKW.js";
import { useAttribute, useId, useMergeRefs, useSafeLayoutEffect, useWrapElement } from "./HNHAUIIY.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/combobox/combobox-list.tsx
import { invariant, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
var useComboboxList = createHook(function useComboboxList2(_a) {
  var _b = _a,
    store = _b.store,
    alwaysVisible = _b.alwaysVisible,
    props = __objRest(_b, ["store", "alwaysVisible"]);
  var scopedContext = useComboboxScopedContext(true);
  var context = useComboboxContext();
  store = store || context;
  var scopedContextSameStore = !!store && store === scopedContext;
  invariant(store, process.env.NODE_ENV !== "production" && "ComboboxList must receive a `store` prop or be wrapped in a ComboboxProvider component.");
  var ref = useRef(null);
  var id = useId(props.id);
  var mounted = store.useState("mounted");
  var hidden = isHidden(mounted, props.hidden, alwaysVisible);
  var style = hidden ? __spreadProps(__spreadValues({}, props.style), {
    display: "none"
  }) : props.style;
  var multiSelectable = store.useState(function (state) {
    return Array.isArray(state.selectedValue);
  });
  var role = useAttribute(ref, "role", props.role);
  var isCompositeRole = role === "listbox" || role === "tree" || role === "grid";
  var ariaMultiSelectable = isCompositeRole ? multiSelectable || void 0 : void 0;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasListboxInside = _useState2[0],
    setHasListboxInside = _useState2[1];
  var contentElement = store.useState("contentElement");
  useSafeLayoutEffect(function () {
    if (!mounted) return;
    var element = ref.current;
    if (!element) return;
    if (contentElement !== element) return;
    var callback = function callback() {
      setHasListboxInside(!!element.querySelector("[role='listbox']"));
    };
    var observer = new MutationObserver(callback);
    observer.observe(element, {
      subtree: true,
      childList: true,
      attributeFilter: ["role"]
    });
    callback();
    return function () {
      return observer.disconnect();
    };
  }, [mounted, contentElement]);
  if (!hasListboxInside) {
    props = __spreadValues({
      role: "listbox",
      "aria-multiselectable": ariaMultiSelectable
    }, props);
  }
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(ComboboxScopedContextProvider, {
      value: store,
      children: /* @__PURE__ */jsx(ComboboxListRoleContext.Provider, {
        value: role,
        children: element
      })
    });
  }, [store, role]);
  var setContentElement = id && (!scopedContext || !scopedContextSameStore) ? store.setContentElement : null;
  props = __spreadProps(__spreadValues({
    id: id,
    hidden: hidden
  }, props), {
    ref: useMergeRefs(setContentElement, ref, props.ref),
    style: style
  });
  return removeUndefinedValues(props);
});
var ComboboxList = forwardRef(function ComboboxList2(props) {
  var htmlProps = useComboboxList(props);
  return createElement(TagName, htmlProps);
});
export { useComboboxList, ComboboxList };